import { type JSX,useCallback, useState } from 'react';
import { NavigateFunction,useNavigate } from 'react-router-dom';

import { Theme,useTheme } from '@mui/material/styles';
import { AsyncThunkAction } from '@reduxjs/toolkit';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { AppDispatch } from 'app/store';
import { DEFAULT_SUCCESS_MESSAGE } from 'common/components/EditDrawer/EditDrawer';
import {
    closeDrawer,
    selectConflictErrorOrigin,
    selectCurrentDrawerId,
    selectCurrentOperation,
    selectNameInConflict,
    selectOpen,
    setNameInConflict } from 'common/components/EditDrawer/editDrawerSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import {
    IEditingItemInfo,
    Operations } from 'common/types/TypeDefs';
import { handleEditDrawerError } from 'common/utils/errorHandlers';
import { ConflictErrorOrigin } from 'common/utils/LicenseAclEntriesErrorHelpers';
import { OptionsObject, SnackbarKey,SnackbarMessage, useSnackbar } from 'notistack';

export interface IConstructDrawerContext {
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    handleSaveBtnEnabled?: () => void;
}

export interface IDrawerContext extends IConstructDrawerContext {
    dispatch: AppDispatch;
    enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey;
    navigate: NavigateFunction;
    setProcessing: (processing: boolean) => void;
    action?: Operations;
}

// Hook return types for type safety
export interface UseCreateDrawerContextReturn<TDrawerProperties, PostArg, PostReturned> {
    // Core utilities
    dispatch: AppDispatch;
    enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey;
    navigate: NavigateFunction;
    theme: Theme;

    // State
    processing: boolean;
    setProcessing: (processing: boolean) => void;
    nameInConflict: boolean;
    conflictErrorOrigin?: ConflictErrorOrigin;
    isOpenDrawer: boolean;
    currentId: string | undefined;
    action: Operations.Create | Operations.Edit | Operations.Link | undefined;

    // Props
    entityName: string;
    drawerProps: TDrawerProperties;

    // Handlers
    createSuccessHandler: (postResult: PostReturned, showSuccessSnackbar?: boolean) => void;
    createFailureHandler: (error: { message?: string; payload?: { message?: string; errorOrigin?: ConflictErrorOrigin } }) => void;
    postCallback: (
        postFunction: (arg: PostArg) => AsyncThunkAction<PostReturned, PostArg, Record<string, unknown>>,
        getPostOptions: () => PostArg | null,
        successHandler?: (postResult: PostReturned) => void,
        failureHandler?: (error: unknown) => void
    ) => void;
}

export interface UseDrawerContextReturn<TEditingItem, TDrawerProperties, PostArg, PostReturned, PutArg, PutReturned>
    extends UseCreateDrawerContextReturn<TDrawerProperties, PostArg, PostReturned> {
    editingItemInfo?: IEditingItemInfo<TEditingItem>;
    editSuccessHandler: (putResult: PutReturned) => void;
    editFailureHandler: (error: { message?: string }) => void;
    putCallback: (
        putFunction: (arg: PutArg) => AsyncThunkAction<PutReturned, PutArg, Record<string, unknown>>,
        getPutOptions: () => PutArg | null,
        successHandler?: (putResult: PutReturned) => void,
        failureHandler?: (error: unknown) => void
    ) => void;
    constructDrawer: (
        getCreateContent: () => JSX.Element,
        getEditContent: () => JSX.Element
    ) => JSX.Element;
}

/**
 * Custom hook for create-only drawer operations.
 * Use this when you only need create/link functionality.
 */
export function useCreateDrawerContext<TDrawerProperties, PostArg, PostReturned>(
    entityName: string,
    drawerProps: TDrawerProperties
): UseCreateDrawerContextReturn<TDrawerProperties, PostArg, PostReturned> {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const theme = useTheme();

    const [processing, setProcessing] = useState<boolean>(false);

    const action = useAppSelector(selectCurrentOperation) as Operations.Create | Operations.Edit | Operations.Link | undefined;
    const nameInConflict = useAppSelector(selectNameInConflict) ?? false;
    const conflictErrorOrigin = useAppSelector(selectConflictErrorOrigin);
    const isOpenDrawer = useAppSelector(selectOpen);
    const currentId = useAppSelector(selectCurrentDrawerId);

    const createSuccessHandler = useCallback((postResult: PostReturned, showSuccessSnackbar = true) => {
        dispatch(setNameInConflict(false));
        dispatch(closeDrawer());
        if (showSuccessSnackbar) {
            const operationName = (action === Operations.Create) ? 'created' : 'linked';
            enqueueSnackbar(`The ${entityName} was successfully ${operationName}.`, { variant: 'success' });
        }
    }, [dispatch, action, entityName, enqueueSnackbar]);

    const createFailureHandler = useCallback((error: { message?: string; errorOrigin?: ConflictErrorOrigin; payload?: { message?: string; errorOrigin?: ConflictErrorOrigin } }) => {
        const errorData = error?.payload || error;
        // errorOrigin can be directly on error (from rejectWithValue) or in payload
        const errorOrigin = error?.errorOrigin || error?.payload?.errorOrigin;
        handleEditDrawerError(
            action ?? Operations.Create,
            extractStatusCode(errorData.message || error.message || ''),
            entityName,
            dispatch,
            enqueueSnackbar,
            errorOrigin
        );
    }, [action, entityName, dispatch, enqueueSnackbar]);

    const postCallback = useCallback((
        postFunction: (arg: PostArg) => AsyncThunkAction<PostReturned, PostArg, Record<string, unknown>>,
        getPostOptions: () => PostArg | null,
        successHandler?: (postResult: PostReturned) => void,
        failureHandler?: (error: unknown) => void
    ) => {
        const postOptions = getPostOptions();
        if (!postOptions) {
            return;
        }
        setProcessing(true);
        dispatch(postFunction(postOptions))
            .unwrap()
            .then((postResult: PostReturned) => {
                if (successHandler) {
                    successHandler(postResult);
                } else {
                    createSuccessHandler(postResult);
                }
            })
            .catch((error) => {
                if (failureHandler) {
                    failureHandler(error);
                } else {
                    createFailureHandler(error);
                }
            })
            .finally(() => {
                setProcessing(false);
            });
    }, [dispatch, createSuccessHandler, createFailureHandler]);

    return {
        dispatch,
        enqueueSnackbar,
        navigate,
        theme,
        processing,
        setProcessing,
        nameInConflict,
        conflictErrorOrigin,
        isOpenDrawer,
        currentId,
        action,
        entityName,
        drawerProps,
        createSuccessHandler,
        createFailureHandler,
        postCallback
    };
}

/**
 * Custom hook for drawer operations that support both create and edit.
 * Use this when you need full CRUD functionality in a drawer.
 */
export function useDrawerContext<
    TEditingItem,
    TDrawerProperties,
    PostArg,
    PostReturned,
    PutArg,
    PutReturned
>(
    entityName: string,
    drawerProps: TDrawerProperties,
    editingItemInfo?: IEditingItemInfo<TEditingItem>
): UseDrawerContextReturn<TEditingItem, TDrawerProperties, PostArg, PostReturned, PutArg, PutReturned> {
    const createContext = useCreateDrawerContext<TDrawerProperties, PostArg, PostReturned>(
        entityName,
        drawerProps
    );

    const { dispatch, enqueueSnackbar, action } = createContext;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const editSuccessHandler = useCallback((putResult: PutReturned) => {
        dispatch(setNameInConflict(false));
        dispatch(closeDrawer());
        enqueueSnackbar(DEFAULT_SUCCESS_MESSAGE, { variant: 'success' });
    }, [dispatch, enqueueSnackbar]);

    const editFailureHandler = useCallback((error: { message?: string }) => {
        handleEditDrawerError(
            Operations.Edit,
            extractStatusCode(error.message || ''),
            createContext.entityName,
            dispatch,
            enqueueSnackbar
        );
    }, [createContext.entityName, dispatch, enqueueSnackbar]);

    const putCallback = useCallback((
        putFunction: (arg: PutArg) => AsyncThunkAction<PutReturned, PutArg, Record<string, unknown>>,
        getPutOptions: () => PutArg | null,
        successHandler?: (putResult: PutReturned) => void,
        failureHandler?: (error: unknown) => void
    ) => {
        if (!editingItemInfo) {
            return;
        }
        const putOptions = getPutOptions();
        if (!putOptions) {
            return;
        }
        createContext.setProcessing(true);
        dispatch(putFunction(putOptions))
            .unwrap()
            .then((putResult) => {
                if (successHandler) {
                    successHandler(putResult);
                } else {
                    editSuccessHandler(putResult);
                }
            })
            .catch((error) => {
                if (failureHandler) {
                    failureHandler(error);
                } else {
                    editFailureHandler(error);
                }
            })
            .finally(() => {
                createContext.setProcessing(false);
            });
    }, [editingItemInfo, dispatch, createContext, editSuccessHandler, editFailureHandler]);

    const constructDrawer = useCallback((
        getCreateContent: () => JSX.Element,
        getEditContent: () => JSX.Element
    ): JSX.Element => {
        if (action === Operations.Create || action === Operations.Link) {
            return getCreateContent();
        } else if (action === Operations.Edit) {
            if (!editingItemInfo) {
                return <></>;
            }
            return getEditContent();
        }
        return <></>;
    }, [action, editingItemInfo]);

    return {
        ...createContext,
        editingItemInfo,
        editSuccessHandler,
        editFailureHandler,
        putCallback,
        constructDrawer
    };
}
